import classnames from 'classnames';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import { arrayOf, func } from 'prop-types';
import React from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';

import CategorySelect from '../../generic/CategorySelect';
import createModalToggle from '../../generic/create-modal-toggle';
import DatePicker from '../../generic/DatePicker';
import MultiSelect from '../../generic/MultiSelect';
import SingleSelect from '../../generic/SingleSelect';
import { InfoTooltip } from '../../generic/Tooltip';
import { campaignLineShape, optionShape } from '../../prop-types';
import { genders } from '../../quotes/constants';
import convertToOption from '../../utils/convert-to-option';
import { getMomentDate } from '../../utils/date-helpers';
import { targetingOptionsForBulkEditor } from '../utils';
import HELP_TEXTS from '../utils/help-texts';

export const ALL_TEXT_ASSETS = [
  { assetName: 'subTitle', label: 'Headline' },
  { assetName: 'title', label: 'Subline' },
  { assetName: 'desc', label: 'Description' },
  { assetName: 'cta', label: 'CTA' },
];

function getPricingModels(campaignLine) {
  return get(campaignLine, ['product', 'possiblePriceModels'], []);
}

function getCompatiblePricingModels(campaignLines) {
  return intersection(...campaignLines.map(getPricingModels));
}

class CampaignLinesBulkEditor extends React.Component {
  state = {
    gender: '',
    startDate: null,
    endDate: null,
    categories: [],
    languages: [get(this.props.availableLanguages, '[0].value')],
    title: '',
    subTitle: '',
    cta: '',
    desc: '',
    landingPageOrigin: '',
    landingPagePathname: '',
    landingPageFilter: '',
    productTag: '',
    activeTab: '1',
  };

  handleLandingPageUpdate = () => {
    const { onTextAssetChange, onPartialLPChange } = this.props;
    const {
      landingPageOrigin,
      languages,
      landingPagePathname,
      landingPageFilter,
    } = this.state;

    if (landingPageOrigin && landingPagePathname && landingPageFilter) {
      onTextAssetChange(
        languages,
        'landingPage',
        `${landingPageOrigin}/${landingPagePathname}/${landingPageFilter}`,
      );
      return;
    }
    if (landingPageOrigin && landingPagePathname) {
      onTextAssetChange(
        languages,
        'landingPage',
        `${landingPageOrigin}/${landingPagePathname}`,
      );
      return;
    }
    if (landingPageOrigin) {
      onPartialLPChange(languages, 'landingPageOrigin', landingPageOrigin);
    }
    if (landingPagePathname) {
      onPartialLPChange(languages, 'landingPagePathname', landingPagePathname);
    }
    if (landingPageFilter) {
      onPartialLPChange(languages, 'landingPageFilter', landingPageFilter);
    }
  };

  update = event => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  toggle(tab) {
    if (this.state.activeTab !== tab) {
      this.setState({
        activeTab: tab,
      });
    }
  }

  render() {
    const {
      availableLanguages,
      campaignLines,
      onChange,
      onChangeTargeting,
      onTextAssetChange,
      toggle,
    } = this.props;
    const {
      gender,
      startDate,
      endDate,
      categories,
      languages,
      landingPageOrigin,
      landingPagePathname,
      landingPageFilter,
      pricing,
      targeting,
      productTag,
    } = this.state;

    const pricingModels = getCompatiblePricingModels(campaignLines);
    const pricingOptions = pricingModels.map(convertToOption);
    const targetingOptions = targetingOptionsForBulkEditor(campaignLines);

    return (
      <>
        <ModalHeader toggle={toggle}>
          Edit the selected campaign lines
        </ModalHeader>
        <ModalBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: this.state.activeTab === '1' })}
                onClick={() => {
                  this.toggle('1');
                }}
              >
                Media fields
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={this.state.activeTab} className="pt-3">
            <TabPane tabId="1">
              <Row>
                <Col sm="12">
                  <Form>
                    <FormGroup row className="align-items-center">
                      <Label for="bulk.startDate" sm={2}>
                        Dates
                      </Label>
                      <Col sm={8}>
                        <DatePicker
                          id="bulk.startDate"
                          name="startDate"
                          value={startDate}
                          onChange={this.update}
                        />
                        <span className="px-1">-</span>
                        <DatePicker
                          id="bulk.endDate"
                          name="endDate"
                          value={endDate}
                          onChange={this.update}
                          initialVisibleMonth={() => getMomentDate(startDate)}
                        />
                      </Col>
                      <Col sm={2}>
                        <Button
                          onClick={() => {
                            if (startDate) onChange('startDate', startDate);
                            if (endDate) onChange('endDate', endDate);
                          }}
                        >
                          Update
                        </Button>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Label for="bulk.gender" sm={2}>
                        Gender
                      </Label>
                      <Col sm={8}>
                        <SingleSelect
                          options={genders}
                          id="bulk.gender"
                          name="gender"
                          value={gender}
                          className="c-campaign-line-select"
                          onChange={this.update}
                        />
                      </Col>
                      <Col sm={2}>
                        <Button onClick={() => onChange('gender', gender)}>
                          Update
                        </Button>
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Label for="bulk.categories" sm={2}>
                        Categories
                      </Label>
                      <Col sm={8}>
                        <CategorySelect
                          id="bulk.categories"
                          name="categories"
                          value={categories}
                          className="c-campaign-line-select"
                          onChange={this.update}
                        />
                      </Col>
                      <Col sm={2}>
                        <Button
                          onClick={() => onChange('categories', categories)}
                        >
                          Update
                        </Button>
                      </Col>
                    </FormGroup>

                    {pricingOptions.length > 0 && (
                      <FormGroup row>
                        <Label for="bulk.pricing" sm={2}>
                          Pricing
                        </Label>
                        <Col sm={8}>
                          <SingleSelect
                            id="bulk.pricing"
                            name="pricing"
                            value={pricing}
                            options={pricingOptions}
                            className="c-campaign-line-select"
                            onChange={this.update}
                          />
                        </Col>
                        <Col sm={2}>
                          <Button onClick={() => onChange('pricing', pricing)}>
                            Update
                          </Button>
                        </Col>
                      </FormGroup>
                    )}

                    <FormGroup row>
                      <Label for="bulk.targeting" sm={2}>
                        Targeting
                        <span className="ml-1">
                          <InfoTooltip target="budget-split-tooltip">
                            {HELP_TEXTS.bulkEditor.targeting}
                          </InfoTooltip>
                        </span>
                      </Label>
                      <Col sm={8}>
                        <SingleSelect
                          id="bulk.targeting"
                          name="targeting"
                          value={targeting}
                          options={targetingOptions}
                          className="c-campaign-line-select"
                          onChange={this.update}
                          readOnly={targetingOptions.length === 0}
                        />
                      </Col>
                      <Col sm={2}>
                        <Button
                          onClick={() =>
                            onChangeTargeting('targeting', targeting)
                          }
                        >
                          Update
                        </Button>
                      </Col>
                    </FormGroup>

                    <FormGroup row>
                      <Label for="bulk.productTag" sm={2}>
                        Product Tag
                      </Label>
                      <Col sm={8}>
                        <Input
                          id="bulk.productTag"
                          name="productTag"
                          value={productTag}
                          className="c-campaign-line-select"
                          onChange={this.update}
                        />
                      </Col>
                      <Col sm={2}>
                        <Button
                          onClick={() => onChange('productTag', productTag)}
                        >
                          Update
                        </Button>
                      </Col>
                    </FormGroup>
                    {availableLanguages.length > 0 && (
                      <>
                        <hr className="mt-4 mb-3" />

                        <FormGroup row className="mt-2">
                          <Label
                            for="bulk.language"
                            className="d-flex align-items-center"
                            sm={12}
                          >
                            <span className="text-nowrap mr-4">
                              Upload the text elements for
                            </span>
                            <MultiSelect
                              options={availableLanguages}
                              id="bulk.languages"
                              name="languages"
                              value={languages}
                              className="c-campaign-line-select w-50 mr-3"
                              onChange={this.update}
                              addAllOption
                              selectAllLabel="Select all languages"
                              isClearable
                              inlineAddAll
                            />
                          </Label>
                        </FormGroup>
                        <FormGroup row>
                          <Label for="bulk.landingPage" sm={2}>
                            LP
                          </Label>
                          <Col sm={8} className="d-flex align-items-center">
                            <Input
                              id="bulk.landingPageOrigin"
                              name="landingPageOrigin"
                              placeholder="e.g. https://www.zalando.de"
                              value={landingPageOrigin}
                              onChange={this.update}
                            />
                            <span className="mx-1">/</span>
                            <Input
                              id="bulk.landingPagePathname"
                              name="landingPagePathname"
                              placeholder="e.g. men/xyz"
                              value={landingPagePathname}
                              onChange={this.update}
                            />
                            <span className="mx-1">/</span>
                            <Input
                              id="bulk.landingPageFilter"
                              name="landingPageFilter"
                              placeholder="e.g. ?sales=true"
                              value={landingPageFilter}
                              onChange={this.update}
                            />
                          </Col>
                          <Col sm={2}>
                            <Button onClick={this.handleLandingPageUpdate}>
                              Update
                            </Button>
                          </Col>
                        </FormGroup>
                        {ALL_TEXT_ASSETS.map(
                          ({ assetName, label, isCountdownField }) => {
                            const stateValue = this.state[assetName];
                            return (
                              !isCountdownField && (
                                <FormGroup key={assetName} row>
                                  <Label for={`bulk.${assetName}`} sm={2}>
                                    {label}{' '}
                                  </Label>
                                  <Col sm={8}>
                                    <Input
                                      id={`bulk.${assetName}`}
                                      name={assetName}
                                      value={stateValue}
                                      onChange={this.update}
                                    />
                                  </Col>
                                  <Col sm={2}>
                                    <Button
                                      onClick={() => {
                                        onTextAssetChange(
                                          languages,
                                          assetName,
                                          stateValue,
                                        );
                                      }}
                                    >
                                      Update
                                    </Button>
                                  </Col>
                                </FormGroup>
                              )
                            );
                          },
                        )}
                      </>
                    )}
                  </Form>
                </Col>
              </Row>
            </TabPane>
          </TabContent>
        </ModalBody>
      </>
    );
  }
}

CampaignLinesBulkEditor.propTypes = {
  campaignLines: arrayOf(campaignLineShape.isRequired).isRequired,
  onChange: func.isRequired,
  onChangeTargeting: func.isRequired,
  onTextAssetChange: func.isRequired,
  onPartialLPChange: func.isRequired,
  availableLanguages: arrayOf(optionShape.isRequired).isRequired,
  toggle: func.isRequired,
};

export default createModalToggle(
  'Edit selected lines',
  CampaignLinesBulkEditor,
  { outline: true },
  { size: 'lg' },
);
